import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { tokens } from "../../utils";

/**
 * gordon-0925-P0-lock-rules-fair-order: renders the upfront list of every
 * required unlock step ("1) answer 3 questions 2) pay $5") plus a "Step X of N"
 * progress line, from the BE's `lock_steps` summary
 * ({steps, total_steps, steps_completed, current_step_index, all_satisfied}).
 *
 * Purely additive: `steps` is null/undefined whenever the BE's
 * LOCK_FAIR_ORDER_ENABLED flag is off (or the wheel has 0-1 configured locks),
 * in which case this renders nothing.
 */
const LockStepsSummary = ({ steps }) => {
  if (!steps || !Array.isArray(steps.steps) || steps.steps.length === 0) {
    return null;
  }

  const { steps: stepList, total_steps: totalSteps, current_step_index: currentStepIndex, all_satisfied: allSatisfied } = steps;

  return (
    <Box data-testid="lock-steps-summary" sx={{ mb: 2 }}>
      {!allSatisfied && totalSteps > 0 && (
        <Typography
          data-testid="lock-steps-progress"
          sx={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, mb: 1 }}
        >
          Step {currentStepIndex} of {totalSteps}
        </Typography>
      )}
      <Stack gap={0.5}>
        {stepList.map((step, index) => (
          <Stack
            key={step.type}
            direction="row"
            gap={1}
            alignItems="center"
            data-testid={`lock-step-${step.type}`}
          >
            {step.satisfied ? (
              <CheckCircleIcon
                fontSize="small"
                htmlColor={tokens?.brand?.primaryActive || "#0F52BA"}
                aria-label={`${step.label} complete`}
              />
            ) : (
              // T22.331: an empty circle sat right above the quiz's own True/False
              // radios and read as one more unchecked radio. A lock says "step
              // still to do" without looking clickable.
              <LockOutlinedIcon
                fontSize="small"
                color="disabled"
                aria-label={`${step.label} not yet complete`}
              />
            )}
            <Typography
              sx={{
                fontFamily: "DM Sans",
                fontSize: 13,
                textDecoration: step.satisfied ? "line-through" : "none",
                color: step.satisfied ? "text.secondary" : "text.primary",
              }}
            >
              {index + 1}) {step.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default LockStepsSummary;
